import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { IntermediateElement } from "../../../../../http/vertigrip/types/intermediateElements";
import { isPassingThrough } from "../../../../../http/vertigrip/types/systemTypes";
import { useIntermediateElementsApi } from "../../../../../http/vertigrip/useIntermediateElements";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateIntermediateElementModalDialogButton from "./CreateIntermediateElementModalDialogButton";
import EditIntermediateElementModalDialog from "./EditIntermediateElementModalDialog";

type Props = {
  active?: boolean;
};

const SystemTypeIntermediateElements: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { systemTypeId } = useParams<{ systemTypeId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "systemTypeIntermediateElements" });
  const aborter = useRef(new AbortController());
  const intermedateElementsApi = useIntermediateElementsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "systemTypeIntermediateElements");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await intermedateElementsApi.updateAdminIntermediateElementVisibility(id, { visibility }, { systemTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (intermediateElementId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await intermedateElementsApi.updateAdminIntermediateElementOrder(
        intermediateElementId,
        { order: newOrder },
        { systemTypeId },
      );
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [idItemToEdit, setIdItemToEdit] = useState<string>();
  const [itemToDelete, setItemToDelete] = useState<IntermediateElement>();
  const { accessesAction, accessesDialog } = useEntityAccesses<IntermediateElement>("vertigrip.intermediate_element");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = intermedateElementsApi.useSystemTypeAdminIntermediateElements(
    systemTypeId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.intermediateElements.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.intermediateElements.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateIntermediateElementModalDialogButton systemTypeId={systemTypeId} />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<IntermediateElement>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.steelVersions.singular}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th align="left">{i18n.translation.installationTypes.singular}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.id}`}>
                {item.code}
              </Link>
            </td>
            <td>{item.description}</td>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/vertigrip_steel_versions/${item.vertigripSteelVersion?.id}`}
              >
                {item.vertigripSteelVersion?.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
            <td>
              {isPassingThrough(item.systemType) ? (
                item.fixed ? (
                  i18n.translation.common.fixed
                ) : (
                  i18n.translation.common.removable
                )
              ) : item.installationType?.name ? (
                <Link to={`/admin/systems/${systemId}/installation_types/${item.installationType?.id}`}>
                  {item.installationType?.name}
                </Link>
              ) : undefined}
            </td>
          </>
        )}
        onRowClick={(item) => setIdItemToEdit(item.id)}
        rowActions={(item) => [
          getEditAction(i18n, () => setIdItemToEdit(item.id)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.intermediateElements.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                intermedateElementsApi
                  .deleteAdminIntermediateElement(
                    itemToDelete.id,
                    itemToDelete.vertigripSteelVersion.id,
                    itemToDelete.installationType?.id,
                    itemToDelete.systemType?.id,
                  )
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.intermediateElements.questions.delete}
        </ModalDialog>
      )}
      <EditIntermediateElementModalDialog
        onClose={() => setIdItemToEdit(undefined)}
        systemTypeId={systemTypeId}
        intermediateElementId={idItemToEdit}
      />
    </FlexContent>
  );
};

export default SystemTypeIntermediateElements;
